// import { DialogType, enableDialog } from "@/components/cdialog/dialog";
// import { errorMessages } from "../error_messages";
import { FakeSocketModel } from "@hid/fakesocket/fakesocket.ts";
import { generateMessageID } from "@libs/utils/tools";
import { DriverSocketMsgType } from "@libs/types/message";
import { HelperSocketMsgType } from "../../src/service/helper/message";
// import i18n from "@/languages/i18n";
import { BaseSocketMsg, MessageHandler, SocketConfig, SocketEventHandlers, SocketMessageType } from "./types";
import { isElectron } from "@libs/utils/envUtils";
// const { t } = i18n.global;
export class SocketService {
    private ws: WebSocket | FakeSocketModel | null = null
    private messageHandlers = new Map<SocketMessageType, MessageHandler>();
    private resolveList: { [index: string]: (value?: any) => void } = {}
    private rejectList: { [index: string]: (value?: any) => void } = {}

    private reconnectAttemptCount = 0;                      // 记录当前重连的次数                                      
    private reconnectTimer: NodeJS.Timeout | null = null;   // 重连定时器
    private wsUrl: string | null = null;                    // ws地址
    private config: SocketConfig = {
        autoReconnect: false,
        reconnectAttempts: 0,
        reconnectDelay: 0,
    }
    private msgErrorCallback: (error: string) => void = (_: string) => { }

    private eventHandlers: SocketEventHandlers = {
        onOpen: () => { },
        onClose: () => { },
        onError: () => { },
    }

    constructor(config?: SocketConfig) {
        //{ ...this.config, ...config } 合并config
        this.config = { ...this.config, ...config };
    }

    connect(obj: WebSocket | FakeSocketModel) {
        this.disconnect();
        this.ws = obj;
        // 如果是WebSocket对象
        if (this.ws instanceof WebSocket) {
            this.wsUrl = this.ws.url;
        }
        this.setupEventHandlers()
    }
    disconnect() {
        if (this.ws) {
            this.clearEventHandlers();
            if (this.ws instanceof WebSocket) {
                this.ws.close();
            }
            this.ws = null;
        }
    }

    // Partial：将SocketEventHandlers中的所有属性变为可选的
    setEventHandlers(handlers: Partial<SocketEventHandlers>): void {
        this.eventHandlers = { ...this.eventHandlers, ...handlers };
    }

    setMsgErrorCallback(callback: (error: string) => void) {
        this.msgErrorCallback = callback;
    }


    private setupEventHandlers() {
        if (this.ws == null) throw new Error("WebSocket is not initialized")

        this.ws.onopen = (e: Event) => {
            this.handleConnectSuccess(e);
        }
        this.ws.onclose = (e: CloseEvent) => {
            this.handleDisconnect(e);
        }
        this.ws.onerror = (e: Event) => {
            this.eventHandlers.onError(e);
        }
        this.ws.onmessage = (e: MessageEvent) => {
            this.handleSocketMessage(e);
        }
    }
    private clearEventHandlers(): void {
        if (!this.ws) return;

        this.ws.onopen = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
    }

    private handleConnectSuccess(event: Event) {
        this.clearReconnectTimer();
        this.reconnectAttemptCount = 0;
        this.eventHandlers.onOpen(event);
    }

    private handleDisconnect(event: CloseEvent) {
        this.eventHandlers.onClose(event);
        this.handleAutoReconnect();
    }

    private handleSocketMessage(event: MessageEvent) {
        try {
            const message: BaseSocketMsg = JSON.parse(event.data);
            this.processIncomingMessage(message)
        } catch (error) {
            console.log("error", error)
        }
    }

    private processIncomingMessage(message: BaseSocketMsg) {
        const errorMessage = message.Error ?? message.Err;
        const handler = this.messageHandlers.get(message.Type);
        if (errorMessage) {
            this.handleFailedMessage(message.ID, errorMessage, handler);
        } else {
            this.handleSuccessfulMessage(message, handler);
        }
    }

    private handleSuccessfulMessage(message: BaseSocketMsg, handler?: MessageHandler) {
        this.resolve(message)
        handler?.onSuccess(message.Data);
    }

    private handleFailedMessage(messageId: string, error: string, handler?: MessageHandler) {
        this.reject(messageId, error);
        handler?.onFailure(error);
        this.msgErrorCallback(error);
        //this.showErrorNotification(error);
    }

    // private showErrorNotification(errMessage: string) {
    //     const errMsg = errorMessages[errMessage] ? t(errorMessages[errMessage].i18n) : errMessage
    //     enableDialog(errMsg, DialogType.notice, false)
    // }

    private resolve(msg: BaseSocketMsg) {
        if (this.resolveList[msg.ID]) {
            this.resolveList[msg.ID](msg.Data)
        }
    }

    private reject(ID: string, error: string) {
        if (this.rejectList[ID]) {
            this.rejectList[ID](error)
        }
    }

    private handleAutoReconnect() {
        if (!this.shouldAttemptReconnect()) return;

        this.reconnectAttemptCount++;
        console.log(`Reconnection attempt ${this.reconnectAttemptCount}/${this.config.reconnectAttempts}`);

        this.reconnectTimer = setTimeout(() => {
            this.createNewConnection();
        }, this.config.reconnectDelay);
    }

    private shouldAttemptReconnect(): boolean {
        // 如果不是在electron环境下，则不尝试重连
        if (!isElectron()) return false;

        // 如果没有配置自动重连，则不尝试重连
        if (!this.config.autoReconnect) return false;

        // 如果已经达到最大重连次数，则不尝试重连
        if (this.config.reconnectAttempts && this.reconnectAttemptCount >= this.config.reconnectAttempts) {
            console.log("达到最大重连次数");
            //enableDialog("重新连接驱动失败", DialogType.notice, false);
            this.msgErrorCallback("达到最大重连次数")
            this.clearReconnectTimer();
            return false;
        }

        // 如果没有重连的url，则不尝试重连
        if (!this.wsUrl) return false;

        return true;
    }

    private createNewConnection() {
        if (!this.wsUrl) return;
        try {
            const newConnection = new WebSocket(this.wsUrl);
            this.connect(newConnection);
        } catch (error) {
            console.error("Reconnection failed:", error);
            this.handleAutoReconnect();
        }
    }

    private clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    public setHandler(messageType: DriverSocketMsgType | HelperSocketMsgType, onSuccess: (data: any) => void, onFailure?: (error: string) => void) {
        this.messageHandlers.set(messageType, {
            onSuccess: onSuccess ?? (() => { }),
            onFailure: onFailure ?? (() => { }),
        });
    }

    public askType<T>(type: DriverSocketMsgType | HelperSocketMsgType): Promise<T> {
        const message = { Type: type } as BaseSocketMsg
        return this.ask<T>(message)
    }

    public askTypeAndData<T>(message: BaseSocketMsg): Promise<T> {
        return this.ask<T>(message)
    }

    public ask<T>(message: BaseSocketMsg): Promise<T> {
        message.ID = generateMessageID();
        console.log("ask", message)
        this.sendMessage(message)
        return new Promise<T>((resolve, reject) => {
            this.resolveList[message.ID] = resolve
            this.rejectList[message.ID] = reject
        })
    }

    private sendMessage(msg: BaseSocketMsg) {
        if (this.ws == null) {
            throw new Error("WebSocket is not initialized");
        }
        if (this.ws instanceof WebSocket && this.ws.readyState !== WebSocket.OPEN) {
            throw new Error(`WebSocket(url: ${this.ws.url}) is not open, msgType:${msg.Type}`)
        }
        const message = JSON.stringify(msg)
        this.ws.send(message)
    }

    checkSocketIsOpen(): boolean {
        if (this.ws == null) {
            return false
        }
        if (this.ws instanceof WebSocket && this.ws.readyState !== WebSocket.OPEN) {
            return false
        }
        return true
    }
}
